'use client'

import TopicAvatar from '@/components/ui/TopicAvatar'
import { track, trackOutboundArticle } from '@/lib/tracking'
import Link from 'next/link'
import { Fragment } from 'react'

// The both-sides story page (pipeline tasks/story_format.py): What happened,
// From the left, From the right, and the coverage count. Pipeline prose is
// plain text; outlet names become links to the article we cited from them,
// built here as React elements — never injected HTML.

// Case-insensitive name variants for one outlet: "FOX News" must match the
// prose's "Fox News", "The Guardian" / "Guardian" match each other, and
// "Breitbart News" also matches a bare "Breitbart". The bare form is `short`:
// it doesn't link when another capitalized word follows ("Fox Business").
function nameVariants(name) {
  const base = name.trim().replace(/^the\s+/i, '')
  const variants = [
    { text: base, short: false },
    { text: `The ${base}`, short: false },
  ]
  const bare = base.replace(/\s+news$/i, '')
  if (bare !== base && bare.length >= 3) {
    variants.push(
      { text: bare, short: true },
      { text: `The ${bare}`, short: true }
    )
  }
  return variants
}

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// lowercased variant -> { key, article }. First cited article per outlet wins.
function buildOutletLinks(articles, citedIds) {
  const byId = new Map((articles || []).map((a) => [a.id, a]))
  const links = new Map()
  for (const id of citedIds || []) {
    const a = byId.get(Number(id))
    const name = a?.outlet?.name
    if (!a?.url || !name) continue
    const key = a.outlet.slug || name.toLowerCase()
    for (const v of nameVariants(name)) {
      const lower = v.text.toLowerCase()
      if (!links.has(lower))
        links.set(lower, { key, article: a, short: v.short })
    }
  }
  return links
}

function buildMatcher(links) {
  if (!links.size) return null
  // Longest first, so "The New York Post" wins over "New York Post".
  const alts = [...links.keys()]
    .sort((x, y) => y.length - x.length)
    .map(escapeRegExp)
    .join('|')
  // Letter/digit boundaries without lookbehind: the prefix char is captured
  // (group 1) and re-emitted as text; the suffix is a lookahead.
  return new RegExp(`(^|[^\\p{L}\\p{N}])(${alts})(?=$|[^\\p{L}\\p{N}])`, 'giu')
}

// Split a paragraph into text + outlet links. `linked` is shared across the
// section's paragraphs so each outlet is linked only at its first mention.
function linkify(text, links, matcher, linked, topicSlug) {
  if (!matcher || !text) return text
  const out = []
  let last = 0
  matcher.lastIndex = 0
  let m
  while ((m = matcher.exec(text)) !== null) {
    const nameStart = m.index + m[1].length
    const entry = links.get(m[2].toLowerCase())
    if (!entry || linked.has(entry.key)) continue
    // Matching is case-insensitive ("FOX News" vs "Fox News"), but a name
    // in prose is capitalized: "for that reason" is not the outlet Reason.
    if (!/^\p{Lu}/u.test(m[2].replace(/^the\s+/i, ''))) continue
    const after = text.slice(nameStart + m[2].length)
    if (entry.short && /^\s+\p{Lu}/u.test(after)) continue
    linked.add(entry.key)
    if (nameStart > last) out.push(text.slice(last, nameStart))
    const { article } = entry
    out.push(
      <a
        key={`${entry.key}-${nameStart}`}
        href={article.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          trackOutboundArticle(article, {
            story_slug: topicSlug,
            surface: 'story_format',
          })
        }
        className="font-semibold text-foreground underline decoration-foreground/60 decoration-[1.5px] underline-offset-4 transition-colors hover:decoration-foreground"
      >
        {m[2]}
      </a>
    )
    last = nameStart + m[2].length
  }
  if (last < text.length) out.push(text.slice(last))
  return out.map((part, i) =>
    typeof part === 'string' ? <Fragment key={`t${i}`}>{part}</Fragment> : part
  )
}

// The outlets the two sides' prose names (and links), by slug: "Read the
// coverage" lists them first (the concept, 2026-11-01). Same matching as the links, capital-letter rule included.
export function quotedOutlets(storyFormat, articles) {
  const found = new Set()
  if (!storyFormat) return found
  const links = buildOutletLinks(articles, storyFormat.cited_article_ids)
  const matcher = buildMatcher(links)
  if (!matcher) return found
  for (const text of [storyFormat.from_left, storyFormat.from_right]) {
    if (!text) continue
    matcher.lastIndex = 0
    let m
    while ((m = matcher.exec(text)) !== null) {
      const entry = links.get(m[2].toLowerCase())
      if (!entry) continue
      if (!/^\p{Lu}/u.test(m[2].replace(/^the\s+/i, ''))) continue
      const after = text.slice(m.index + m[1].length + m[2].length)
      if (entry.short && /^\s+\p{Lu}/u.test(after)) continue
      found.add(entry.key)
    }
  }
  return found
}

const paragraphs = (text) =>
  String(text || '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)

// `stretch` keeps the two accent rules the same length so the columns read as
// a pair. It's off for an uncovered wing: a 190px red rule hanging off the one
// italic line that says nobody on the right covered this gave an absence the
// visual weight of an argument. It takes `self-start`, not just dropping
// `h-full` — a grid item stretches to its row by default, so the rule ran the
// full height whether or not the box asked for it.
function Part({ title, accent, stretch = true, children }) {
  return (
    <div className={accent && !stretch ? 'self-start' : undefined}>
      {/* The mocks' side heading (2026-11-01): small caps in the side's
          colour with a 28x6 swatch before it on a desktop; on a phone the
          swatch goes (the 5px rule over the side carries the colour). */}
      <h3 className="mb-2.5 flex items-center gap-2.5 font-sans text-xs font-semibold uppercase tracking-[0.12em] sm:mb-4 sm:text-[13px]">
        {accent && (
          <span
            className={`hidden h-1.5 w-7 shrink-0 sm:inline-block ${accent}`}
            aria-hidden="true"
          />
        )}
        {title}
      </h3>
      {children}
    </div>
  )
}

// The pipeline's fixed empty-wing sentence ("Right-leaning outlets we track
// didn't cover this story.") is a finding, not a telling: render it muted.
const NO_COVERAGE = /outlets we track (?:didn['’]t|did not) cover this story/i

const renderParagraphs = (text, renderText = (p) => p) =>
  paragraphs(text).map((p, i) => (
    <p key={i} className={i > 0 ? 'mt-3' : undefined}>
      {renderText(p)}
    </p>
  ))

// A topic named in the overview (2026-10-15): a dotted underline to its
// topic page. On a pointer device, hovering shows who or what it is, with
// its picture (2026-11-01, Sam); on a phone (no hover) a tap simply opens
// the page, where that sits at the top.
function TopicLink({ topic, children }) {
  return (
    <span className="group relative">
      <Link
        href={`/topic/${topic.slug}`}
        onClick={() =>
          track('topic_click', {
            topic_slug: topic.slug,
            source_surface: 'story_overview',
          })
        }
        className="underline decoration-foreground/50 decoration-dotted decoration-[1.5px] underline-offset-[5px] transition-colors hover:decoration-foreground"
      >
        {children}
      </Link>
      {topic.description && (
        <span
          role="tooltip"
          className="pointer-events-none invisible absolute bottom-full left-0 z-30 mb-2 flex w-72 gap-3 rounded-sm border border-border bg-card px-3 py-2.5 font-sans text-xs leading-snug text-muted-foreground opacity-0 shadow-sm transition-opacity group-hover:visible group-hover:opacity-100 [@media(hover:none)]:hidden"
        >
          <TopicAvatar topic={topic} size="h-10 w-10 text-[11px]" />
          <span className="min-w-0">
            <span className="block text-[13px] font-semibold text-foreground">
              {topic.name}
            </span>
            <span className="block first-letter:uppercase">
              {topic.description}
            </span>
          </span>
        </span>
      )}
    </span>
  )
}

// Each topic's first mention in the overview becomes a TopicLink, in text
// order; `used` carries across paragraphs so a name links once per story.
function linkTopics(text, topics, used) {
  const out = []
  let rest = text
  let key = 0
  for (;;) {
    let hit = null
    for (const t of topics) {
      if (used.has(t.slug)) continue
      const at = rest.indexOf(t.mention)
      // At the same spot the longer mention wins: "Senate Leadership Fund",
      // not the "Senate" inside it.
      if (
        at >= 0 &&
        (!hit ||
          at < hit.at ||
          (at === hit.at && t.mention.length > hit.t.mention.length))
      )
        hit = { at, t }
    }
    if (!hit) break
    used.add(hit.t.slug)
    if (hit.at) out.push(rest.slice(0, hit.at))
    out.push(
      <TopicLink key={key++} topic={hit.t}>
        {hit.t.mention}
      </TopicLink>
    )
    rest = rest.slice(hit.at + hit.t.mention.length)
  }
  if (rest) out.push(rest)
  return out
}

// "What happened": the undisputed facts, as the page's lede. Plain text,
// with the story's lesser-known people and organizations underlined to
// their topic pages (`topics`, from entity-client fetchStoryTopicLinks).
// Full width of the column (2026-09-28): the page's other blocks run edge
// to edge, and a lede stopping short read as a layout mistake.
// Pages from story page v1 (2026-11-03, pipeline tasks/story_page.py) carry
// `bullets`, five one-sentence points; they show as a list. Older pages have
// only the paragraph in `text`.
export function WhatHappened({ text, bullets = null, topics = [] }) {
  const items = Array.isArray(bullets) ? bullets.filter(Boolean) : []
  if (!text && !items.length) return null
  const used = new Set()
  const link = (p) => (topics.length ? linkTopics(p, topics, used) : p)
  // The mocks' lede type: 19px over 1.65 on a desktop, 17px over 1.6 on a
  // phone.
  return (
    <div className="font-serif text-[17px] leading-[1.6] text-foreground sm:text-[19px] sm:leading-[1.65]">
      {items.length ? (
        <ul className="list-disc space-y-2.5 pl-5 marker:text-muted-foreground sm:space-y-3">
          {items.map((b, i) => (
            <li key={i} className="pl-1">
              {link(b)}
            </li>
          ))}
        </ul>
      ) : (
        renderParagraphs(text, link)
      )}
    </div>
  )
}

// From the left / From the right + the coverage count. Outlet names link to
// the article we cited from that outlet (first mention only).
// `stacked` puts the two sides one above the other at every width, for a
// narrow column (How it works); the story page sets them side by side.
export default function StorySides({
  storyFormat,
  articles,
  topicSlug,
  stacked = false,
}) {
  if (!storyFormat) return null
  const links = buildOutletLinks(articles, storyFormat.cited_article_ids)
  const matcher = buildMatcher(links)
  // Shared across both sides: an outlet links at its first mention only.
  const linked = new Set()

  const side = (text) => (
    <div
      className={
        NO_COVERAGE.test(text)
          ? 'font-sans text-sm italic text-muted-foreground'
          : // The mocks' side type: 19px serif over 1.6 on a desktop, 17px
            // on a phone (a 342px measure at 18px is ~34 characters a line).
            'font-serif text-[17px] leading-[1.6] text-foreground sm:text-[19px]'
      }
    >
      {renderParagraphs(text, (p) =>
        linkify(p, links, matcher, linked, topicSlug)
      )}
    </div>
  )

  // The mocks' two sides (2026-11-01). Desktop: one box ruled in ink above
  // and below, the left and the right side by side with a hairline between,
  // each headed "How the left told it" in its colour with a swatch. Phone:
  // no box; each side under a 5px rule in its colour, one above the other.
  // The prose names its outlets, so no outlet lists under the sides (Sam,
  // 2026-11-01: they cluttered it); "Read the coverage" lists them all.
  return (
    <div>
      <div
        className={
          stacked
            ? 'grid gap-8'
            : 'sm:grid sm:grid-cols-2 sm:border-y sm:border-foreground'
        }
      >
        {storyFormat.from_left && (
          <div
            className={
              stacked
                ? ''
                : 'border-t-[5px] border-blue-600 pt-3.5 sm:border-t-0 sm:border-r sm:border-border sm:pb-[34px] sm:pr-9 sm:pt-8'
            }
          >
            <Part
              title={
                <span className="text-blue-600 dark:text-blue-400">
                  How the left told it
                </span>
              }
              accent="bg-blue-600"
              stretch={!NO_COVERAGE.test(storyFormat.from_left)}
            >
              {side(storyFormat.from_left)}
            </Part>
          </div>
        )}
        {storyFormat.from_right && (
          <div
            className={
              stacked
                ? ''
                : 'mt-6 border-t-[5px] border-red-600 pt-3.5 sm:mt-0 sm:border-t-0 sm:pb-[34px] sm:pl-9 sm:pt-8'
            }
          >
            <Part
              title={
                <span className="text-red-600 dark:text-red-400">
                  How the right told it
                </span>
              }
              accent="bg-red-600"
              stretch={!NO_COVERAGE.test(storyFormat.from_right)}
            >
              {side(storyFormat.from_right)}
            </Part>
          </div>
        )}
      </div>
    </div>
  )
}
